import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Formik } from 'formik';

import * as S from './styles';
import { BasicDialog } from '../../../../../components/newStudent/Dialog';
import TitleModal from '../../../../../components/newStudent/Title/TitleModal';
import FieldFormik from '../../../../../components/newStudent/FieldFormik';
import QuillEditor from '../../../../../components/newStudent/InputTextQuill';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import TicketServiceSelect from '../../../../../components/newStudent/TicketServiceSelect/index';
import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../../../components/newStudent/ProviderNewStudent';
import { ticketsServiceNewStudent } from '../../../../../services/newStudent';
import {
  IInternalTicketCreateBody,
  IInternalTicketServiceOption,
} from '../../../../../interfaces/newStudent/ticket.interface';

interface Props {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  onRefreshList: () => void;
}

const initialValues: IInternalTicketCreateBody = {
  title: '',
  serviceId: 0,
  description: '',
};

export default function CreateTicketModal({
  isOpen,
  setIsOpen,
  onRefreshList,
}: Props) {
  const { setIsLoading } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  const [description, setDescription] = useState('');
  const [serviceOptions, setServiceOptions] = useState<
    IInternalTicketServiceOption[]
  >([]);
  const [selectedServiceValues, setSelectedServiceValues] = useState<number[]>(
    [],
  );
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    setDescription('');
    setSelectedServiceValues([]);
    setFiles([]);
    setIsOpen(false);
  };

  const handleLoadServices = async () => {
    setIsLoading(true);
    try {
      const { data, result } = await ticketsServiceNewStudent.getServices();
      if (result) {
        setServiceOptions(data);
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = async (values: IInternalTicketCreateBody) => {
    setIsLoading(true);
    try {
      const body: IInternalTicketCreateBody = {
        title: values.title,
        serviceId: selectedServiceValues[0] ?? 0,
        description,
      };
      const { result } = await ticketsServiceNewStudent.create(body, files);
      if (result) {
        handleClose();
        onRefreshList();
      }
    } catch (error) {
      throw new Error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) return;
    setFiles(Array.from(event.target.files));
  };

  useEffect(() => {
    if (isOpen) {
      setDescription('');
      setSelectedServiceValues([]);
      setFiles([]);
      handleLoadServices();
    }
  }, [isOpen]);

  return (
    <BasicDialog
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      maxWidth="lg"
      fullWidth
      scroll="body"
      handleOnClose={handleClose}
    >
      <S.Container>
        <TitleModal
          title="Novo Requerimento"
          subtitle="Preencha os dados abaixo para abrir um ticket de atendimento."
          setIsOpen={setIsOpen}
          handleOnClose={handleClose}
        />
        <Formik initialValues={initialValues} onSubmit={handleCreate}>
          {(formik) => (
            <S.FormContent>
              <S.FieldsRow>
                <FieldFormik
                  name="title"
                  type="string"
                  label="Título"
                  placeholder="Informe o título do requerimento"
                  value={formik.values.title}
                  onChange={formik.handleChange}
                  widthSize="fullWidth"
                />
                <TicketServiceSelect
                  label="Serviço"
                  groups={serviceOptions}
                  selectedValues={selectedServiceValues}
                  onChange={setSelectedServiceValues}
                />
              </S.FieldsRow>

              <S.EditorWrapper>
                <S.Label>Descrição</S.Label>
                <QuillEditor text={description} setText={setDescription} />
              </S.EditorWrapper>

              <S.AttachmentSection>
                <S.Label>Anexos</S.Label>
                <S.AttachmentRow>
                  <ButtonSimple
                    color="gray"
                    outline
                    widthSize="medium"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Selecionar arquivos
                  </ButtonSimple>
                  <S.FileInput
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileChange}
                  />
                  {files.length > 0 && (
                    <S.FileList>
                      {files.map((file) => (
                        <S.FileName key={`${file.name}-${file.size}`}>
                          {file.name}
                        </S.FileName>
                      ))}
                    </S.FileList>
                  )}
                </S.AttachmentRow>
              </S.AttachmentSection>

              <S.ButtonDiv>
                <ButtonSimple
                  color="primary"
                  widthSize="medium"
                  onClick={() => formik.handleSubmit()}
                >
                  Salvar
                </ButtonSimple>
                <ButtonSimple
                  color="gray"
                  outline
                  widthSize="medium"
                  onClick={handleClose}
                >
                  Cancelar
                </ButtonSimple>
              </S.ButtonDiv>
            </S.FormContent>
          )}
        </Formik>
      </S.Container>
    </BasicDialog>
  );
}
